import { useEffect, useState } from 'react';
import type { AjustesCampanas } from '../../api/ops/campanas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { HORAS } from './editor';
import { erroresAjustes } from './reglas';

/**
 * 12.7 Ajustes de campañas (RV Panel desde «Ajustes» de la línea del título): límite de frecuencia (1 a 60 días; el
 * cumpleaños, una vez al año), ventana de atribución (1 a 30) y franja horaria opcional, **vacía por defecto** (G34): su
 * inicio es la hora de siempre de lo que no tiene hora (G740), sin ella las 10:00. Se guarda con `updateCampaignSettings`
 * e `If-Match` al momento, con «Deshacer» 5 s (G159). El Encargado los ve sin cambiarlos.
 */
export function Ajustes({ ops, negocio, movil, soloLectura, onCerrar }: PropsPantalla & { onCerrar: () => void }) {
    const { puede } = usePermisos();
    const cambia = puede('campanas.ajustes.configurar') && !soloLectura;
    const aviso = useAvisoBreve();
    const [leidos, setLeidos] = useState<{ datos: AjustesCampanas; version: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [frecuencia, setFrecuencia] = useState('7');
    const [atribucion, setAtribucion] = useState('7');
    const [conFranja, setConFranja] = useState(false);
    const [desde, setDesde] = useState('10:00');
    const [hasta, setHasta] = useState('21:00');
    const [intentado, setIntentado] = useState(false);
    const envio = useEnvioRetrasado();

    useEffect(() => {
        let vivo = true;
        ops.getCampaignSettings(negocio)
            .then((r) => {
                if (!vivo) {
                    return;
                }
                setLeidos(r);
                setFrecuencia(String(r.datos.frequency_cap_days));
                setAtribucion(String(r.datos.attribution_window_days));
                setConFranja(r.datos.automatic_send_window !== null);
                setDesde(r.datos.automatic_send_window?.from ?? '10:00');
                setHasta(r.datos.automatic_send_window?.to ?? '21:00');
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    const errores = erroresAjustes({ frecuencia, atribucion, conFranja, desde, hasta });
    const a = leidos?.datos;
    const sinCambios =
        a !== undefined &&
        String(a.frequency_cap_days) === frecuencia.trim() &&
        String(a.attribution_window_days) === atribucion.trim() &&
        (a.automatic_send_window === null ? !conFranja : conFranja && a.automatic_send_window.from === desde && a.automatic_send_window.to === hasta);

    const guardar = () => {
        setIntentado(true);
        if (Object.keys(errores).length > 0 || leidos === null) {
            return;
        }
        const version = leidos.version ?? '';
        const cuerpo = { frequency_cap_days: Number(frecuencia), attribution_window_days: Number(atribucion), automatic_send_window: conFranja ? { from: desde, to: hasta } : null };
        envio.programar({
            texto: 'Ajustes guardados.',
            donde: 'ajustes',
            enviar: () => {
                ops.updateCampaignSettings(negocio, cuerpo, version)
                    .then((r) => { setLeidos(r); })
                    .catch((e: unknown) => {
                        if (e instanceof ErrorApi && e.estado === 412) {
                            aviso({ tipo: 'error', texto: 'Alguien ha cambiado los ajustes: los volvemos a leer.' });
                            setIntento((i) => i + 1);
                            return;
                        }
                        aviso({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof Error ? e.message : 'No se han podido guardar.') });
                    });
            },
            alDeshacer: () => { setIntento((i) => i + 1); },
        });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo="Ajustes de campañas"
            eyebrow="Campañas"
            icono="sliders-horizontal"
            {...(a !== undefined && a.version === 0 ? { chip: 'Por defecto' } : {})}
            accion={cambia ? 'Guardar' : ''}
            inactiva={!cambia || sinCambios}
            onAccion={guardar}
        >
            {error !== null ? (
                <EstadoError seccion="los ajustes" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />
            ) : a === undefined ? (
                <EstadoCargando seccion="los ajustes" />
            ) : (
                <div className="rv-cp-form">
                    {!cambia && (
                        <p className="rv-ca-nota">
                            <Icono nombre="eye" />
                            <span>Con tu rol ves los ajustes. Los cambian el propietario y los administradores.</span>
                        </p>
                    )}
                    {envio.pendiente !== null && <Deshacer clave={envio.pendiente.clave} texto={envio.pendiente.texto} onDeshacer={envio.deshacer} movil={movil} />}
                    <div className="rv-cp-seccion">
                        <h3>
                            <Icono nombre="gauge" /> Límite de frecuencia
                        </h3>
                        <p>Cuántos días tienen que pasar entre dos mensajes automáticos al mismo cliente. El cumpleaños, una vez al año.</p>
                        <Campo etiqueta="Días" tipo="number" modo="numeric" valor={frecuencia} onChange={setFrecuencia} bloqueado={!cambia} error={intentado ? (errores.frecuencia ?? null) : null} ayuda={`Un mensaje automático cada ${frecuencia} días como mucho.`} />
                    </div>
                    <div className="rv-cp-seccion">
                        <h3>
                            <Icono nombre="trending-up" /> Ventana de atribución
                        </h3>
                        <p>Durante cuántos días una reserva o compra se cuenta como posterior a un mensaje.</p>
                        <Campo etiqueta="Días" tipo="number" modo="numeric" valor={atribucion} onChange={setAtribucion} bloqueado={!cambia} error={intentado ? (errores.atribucion ?? null) : null} ayuda={`Reservas y compras en los ${atribucion} días siguientes.`} />
                    </div>
                    <div className="rv-cp-seccion">
                        <h3>
                            <Icono nombre="clock" /> Franja horaria
                        </h3>
                        <p>Opcional. Si la pones, lo que venza fuera sale al empezar la franja. Su inicio es también la hora de siempre de cumpleaños, inactivos y lo que no tiene hora.</p>
                        <Interruptor etiqueta="Poner una franja" valor={conFranja} onChange={setConFranja} bloqueado={!cambia} {...(conFranja ? {} : { nota: 'Sin franja: los que no tienen hora salen a las 10:00' })} />
                        {conFranja && (
                            <div className="rv-cp-fila">
                                <Desplegable etiqueta="Desde" valor={desde} opciones={HORAS.map((h) => ({ valor: h, texto: h }))} onChange={setDesde} bloqueado={!cambia} error={intentado ? (errores.franja ?? null) : null} />
                                <Desplegable etiqueta="Hasta" valor={hasta} opciones={HORAS.map((h) => ({ valor: h, texto: h }))} onChange={setHasta} bloqueado={!cambia} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </PanelLateral>
    );
}
